import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import {
  AdminFetchRetryConfig,
  AdminFetchRetryConfigs,
  AdminRescheduleRetryJob,
  AdminUpdateRetryConfig,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import type {
  FetchRetryConfigPayload,
  FetchRetryConfigsPayload,
  RescheduleRetryJobPayload,
  RetryActionJob,
  RetryConfig,
  RetryConfigDetail,
  RetryConfigPayment,
  RetryConfigsPage,
  UpdateRetryConfigPayload,
} from 'src/interfaces/retryConfig'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { captureSentryError } from 'src/utils/sentry'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { callSecureApi } from './api/callApi'

/**
 * The sagas' own view of their payloads: the required ids are optional here because each handler
 * guards them at runtime — an action can be dispatched with a malformed payload from anywhere.
 * The exported types stay strict for the pages that build them. Same split as `onFetchMarkets`.
 */
type FetchRetryConfigsActionPayload = Omit<FetchRetryConfigsPayload, 'partnerId'> & {
  partnerId?: string
}

type FetchRetryConfigActionPayload = Partial<FetchRetryConfigPayload>

type UpdateRetryConfigActionPayload = Omit<UpdateRetryConfigPayload, 'configId'> & {
  configId?: string
}

type RescheduleRetryJobActionPayload = Partial<RescheduleRetryJobPayload>

/** The shape the detail endpoint answers: the entity with its two relations inlined. */
interface RetryConfigEntityResponse extends RetryConfig {
  retryActionJobs?: RetryActionJob[]
  partnerDriverEarning?: RetryConfigPayment | null
}

const EMPTY_DETAIL: RetryConfigDetail = { config: null, actionJobs: [], payment: null }

export function* onFetchRetryConfigs(action: AsyncAction<FetchRetryConfigsActionPayload>) {
  const partnerId = action.payload?.partnerId

  // The endpoint's `partnerId` is `@IsNotEmpty @IsUUID` — there is no "all partners" listing.
  if (!partnerId) {
    yield put(AdminFetchRetryConfigs.failed(new Error('Missing partnerId')))

    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  const { partnerMarketId, onlyScheduled, limit, offset, sortDirection } = action.payload

  try {
    // Answers `{ data, pagination }`, NOT a raw array — `normalizeApiListPayload` would discard
    // the pagination the grid's `rowCount` depends on, so it is not used here.
    //
    // `onlyScheduled` goes over the wire as the STRING 'true': `callApi`'s query type has no
    // boolean, and the backend transform is literally `value === 'true'`. When false it is
    // `undefined`, which `_.omitBy(query, _.isNil)` drops from the query string entirely.
    const { payload }: { payload: RetryConfigsPage } = yield call(
      callSecureApi,
      Endpoints.AdminFetchRetryConfigs,
      {
        method: 'GET',
        query: {
          partnerId,
          partnerMarketId: partnerMarketId ?? undefined,
          onlyScheduled: onlyScheduled ? 'true' : undefined,
          limit,
          offset,
          sort: `scheduledAt:${sortDirection}`,
        },
      },
    )

    yield put(AdminFetchRetryConfigs.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.retryConfig.onFetchRetryConfigs] error' })

    yield put(AdminFetchRetryConfigs.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load scheduled actions'))
    }
  }
}

export function* onFetchRetryConfig(action: AsyncAction<FetchRetryConfigActionPayload>) {
  const configId = action.payload?.configId

  if (!configId) {
    yield put(AdminFetchRetryConfig.failed(new Error('Missing configId')))

    if (action.next) {
      action.next('Missing configId')
    }

    return
  }

  try {
    const { payload }: { payload: RetryConfigEntityResponse | null } = yield call(
      callSecureApi,
      Endpoints.AdminFetchRetryConfig,
      {
        method: 'GET',
        params: [{ field: ':configId', value: configId }],
      },
    )

    // Split into the three detail cards, the way the Retool transformer did. The list row and
    // this entity are different shapes, which is why the detail is fetched rather than reused.
    let detail: RetryConfigDetail = EMPTY_DETAIL

    if (payload) {
      const {
        retryActionJobs: actionJobs = [],
        partnerDriverEarning: payment = null,
        ...config
      } = payload

      detail = { config, actionJobs, payment }
    }

    yield put(AdminFetchRetryConfig.success(detail))

    if (action.next) {
      action.next(null, detail)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.retryConfig.onFetchRetryConfig] error' })

    yield put(AdminFetchRetryConfig.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load the scheduled action'))
    }
  }
}

export function* onUpdateRetryConfig(action: AsyncAction<UpdateRetryConfigActionPayload>) {
  const configId = action.payload?.configId

  if (!configId) {
    yield put(AdminUpdateRetryConfig.failed(new Error('Missing configId')))

    if (action.next) {
      action.next('Missing configId')
    }

    return
  }

  const { maxAttempts, backoffMultiplier, timeoutMinutes, currentAttempt, disabled } =
    action.payload

  try {
    const { payload }: { payload: RetryConfig } = yield call(
      callSecureApi,
      Endpoints.AdminUpdateRetryConfig,
      {
        method: 'PUT',
        params: [{ field: ':configId', value: configId }],
        body: { maxAttempts, backoffMultiplier, timeoutMinutes, currentAttempt, disabled },
      },
    )

    yield put(AdminUpdateRetryConfig.success(payload))

    yield put(AdminFetchRetryConfig.request({ configId }))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.retryConfig.onUpdateRetryConfig] error' })

    yield put(AdminUpdateRetryConfig.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to update the action config'))
    }
  }
}

export function* onRescheduleRetryJob(action: AsyncAction<RescheduleRetryJobActionPayload>) {
  const configId = action.payload?.configId
  const retryJobId = action.payload?.retryJobId

  if (!configId || !retryJobId) {
    yield put(AdminRescheduleRetryJob.failed(new Error('Missing configId or retryJobId')))

    if (action.next) {
      action.next('Missing configId or retryJobId')
    }

    return
  }

  try {
    const { payload }: { payload: RetryActionJob } = yield call(
      callSecureApi,
      Endpoints.AdminRescheduleRetryJob,
      {
        method: 'PUT',
        params: [
          { field: ':configId', value: configId },
          { field: ':retryJobId', value: retryJobId },
        ],
        body: { newSchedule: action.payload.newSchedule },
      },
    )

    yield put(AdminRescheduleRetryJob.success(payload))

    // Only the detail is refetched here, even though `scheduledAt` is also a LIST column. The
    // list lives on a separate route (`ScheduledActionsTab`), so navigating Back from this detail
    // page unmounts it and remounts the list, whose own fetch effect reloads `scheduledAt` from
    // scratch — a list refetch here would be redundant work against a component that is not
    // even mounted while this saga runs.
    yield put(AdminFetchRetryConfig.request({ configId }))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.retryConfig.onRescheduleRetryJob] error' })

    yield put(AdminRescheduleRetryJob.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to reschedule the job'))
    }
  }
}

export function* retryConfigSaga() {
  // `takeLatest` on both reads: filter changes re-trigger the list rapidly, and a fast
  // back-and-forth between detail pages would otherwise race.
  yield takeLatest(AdminFetchRetryConfigs.type.REQUEST, onFetchRetryConfigs)
  yield takeLatest(AdminFetchRetryConfig.type.REQUEST, onFetchRetryConfig)
  yield takeEvery(AdminUpdateRetryConfig.type.REQUEST, onUpdateRetryConfig)
  yield takeEvery(AdminRescheduleRetryJob.type.REQUEST, onRescheduleRetryJob)
}
